import { forwardRef } from 'react';
import type { InputNumberRef } from '@rc-component/input-number';
import { IDRInput } from './IDRInput';
import { MUAInput } from './MUAInput';
import type { NumberInputProps } from './NumberInput';

/**
 * Mode dinamis untuk {@link DynamicMoneyInput}.
 *
 * - `'IDR'` → delegate ke {@link IDRInput} (integer, no bg).
 * - `'MUA'` → delegate ke {@link MUAInput} (2 desimal, bg biru `#E3F2FD`).
 */
export type MoneyMode = 'IDR' | 'MUA';

/**
 * Props {@link DynamicMoneyInput}.
 *
 * Inherits semua props {@link NumberInputProps} kecuali `numberFormat`/
 * `showControls` (sudah dikunci oleh derivatif). Tambah satu prop wajib:
 * `mode` untuk memilih varian IDR atau MUA secara dinamis.
 */
export interface DynamicMoneyInputProps
  extends Omit<NumberInputProps, 'numberFormat' | 'showControls' | 'mode'> {
  /**
   * Mode aktif. Biasanya disambungkan ke state seperti `mitraLokal`:
   * - `mitraLokal === true`  → `'IDR'`
   * - `mitraLokal === false` → `'MUA'`
   */
  mode: MoneyMode;
}

/**
 * DERIVATIF: Textbox uang yang **switch format secara dinamis** antara
 * IDR dan MUA berdasarkan prop `mode`.
 *
 * Dipakai di tempat-tempat yang format-nya bergantung pada lokasi mitra
 * (lokal vs luar negeri) — mis. field `biayaAdministrasiBank`, `denda`,
 * `saldoAwalHutang` di Bukti Penerimaan/Pengeluaran/Pemindahbukuan, atau
 * `hargaSatuan` di Input Produk Invoice.
 *
 * Implementasi cukup delegasi ke {@link IDRInput} atau {@link MUAInput}
 * sehingga semua aturan family textbox tetap berlaku otomatis (rata kanan,
 * 0→'', negatif minus, separator id-ID, bg biru untuk MUA).
 *
 * **Penting — switching mode**:
 * Saat `mode` berubah dari IDR ke MUA (atau sebaliknya), nilai number tetap
 * di state form, tapi presisi ditampilkan beda (integer vs 2 desimal).
 * Jika nilai sebelumnya `1500` di mode IDR, lalu switch ke MUA → tampil
 * `1.500,00`. Konversi nilai (mis. konversi ke MUA via kurs) menjadi
 * tanggung jawab caller, BUKAN komponen ini.
 *
 * Pemakaian:
 * ```tsx
 * <DynamicMoneyInput
 *   mode={mitraLokal ? 'IDR' : 'MUA'}
 *   value={biayaAdministrasiBank}
 *   onChange={setBiayaAdministrasiBank}
 * />
 * ```
 *
 * Dokumentasi pattern: `.claude/docs/Booku v2/ui-patterns/textbox-pattern.md`
 */
export const DynamicMoneyInput = forwardRef<InputNumberRef, DynamicMoneyInputProps>(
  function DynamicMoneyInput({ mode, ...rest }, ref) {
    return mode === 'IDR' ? (
      <IDRInput ref={ref} {...rest} />
    ) : (
      <MUAInput ref={ref} {...rest} />
    );
  }
);
